/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.tr
import auth.UserIdManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import models.*
import repository.DatabaseResult
import repository.SavingsGoalsRepository
import repository.createSavingsGoalsRepositoryWithAuth
import studio.PaymentFrequency
import kotlin.js.Date as JsDate
import kotlin.random.Random

/**
 * ViewModel for Web Savings Goals
 * Manages savings goals state and operations using the shared KMP repository
 * Uses reactive Flow subscription like iOS/Android for automatic updates
 * NOW WITH SERVER SYNC via JWT authentication
 */
class WebSavingsGoalsViewModel(
    private val repository: SavingsGoalsRepository = createSavingsGoalsRepositoryWithAuth(
        getAuthToken = { ApiService.getSharedAuthToken() },
        getUserId = { ApiService.Companion.getSharedUserId() }
    ),
    private val userIdManager: UserIdManager = UserIdManager(),
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Default)
) {
    var goals: List<SavingsGoal> = emptyList()
        private set

    var isLoading: Boolean = false
        private set

    var errorMessage: String? = null
        private set

    var onStateChanged: (() -> Unit)? = null

    /**
     * Force refresh of goals for a specific user ID
     * Useful when the ViewModel is initialized before the user ID is fully available
     */
    fun refresh(userId: String) {
        scope.launch {
            console.log("🔄 WebViewModel: Refreshing goals for userId=$userId")
            repository.observeActiveGoals(userId).collect { newGoals ->
                console.log("📊 WebViewModel: Received ${newGoals.size} goals for userId=$userId")
                goals = newGoals
                isLoading = false
                onStateChanged?.invoke()
            }
        }
    }
    private var isObserving = false

    init {
        console.log("🔄 Web ViewModel: Initialized (deferred Flow subscription)")
        console.log("   - Shared UserId: ${ApiService.Companion.getSharedUserId()}")
        console.log("   - Shared AuthToken available: ${ApiService.getSharedAuthToken() != null}")
    }

    /**
     * Start observing goals - call this AFTER login to ensure correct userId
     */
    fun startObserving() {
        if (isObserving) {
            console.log("⚠️ Web ViewModel: Already observing, skipping")
            return
        }
        isObserving = true

        // U7-Web: Subscribe to reactive Flow like iOS/Android
        // This ensures goals automatically update when DB changes
        console.log("🔄 Web ViewModel: Setting up reactive goals Flow...")
        scope.launch {
            // CRITICAL: Use authenticated userId from ApiService, not UserIdManager
            // UserIdManager may have stale data from previous sessions
            val userId = ApiService.Companion.getSharedUserId() ?: userIdManager.getUserId()
            val authToken = ApiService.getSharedAuthToken()
            console.log("👤 Web ViewModel: Observing goals for userId=$userId")
            console.log("🔑 Web ViewModel: Auth token ${if (authToken != null) "available (${authToken.take(10)}...)" else "NOT available - goals fetch will fail!"}")

            // FIX: Force a direct server fetch before observing to ensure fresh data
            // This bypasses potential timing issues with repository initialization
            console.log("🔄 Web ViewModel: Forcing getAllGoals to fetch from server...")
            try {
                when (val result = repository.getAllGoals(userId)) {
                    is DatabaseResult.Success -> {
                        console.log("✅ Web ViewModel: Force-fetched ${result.data.size} goals from server")
                    }
                    is DatabaseResult.Error -> {
                        console.error("⚠️ Web ViewModel: Force-fetch failed: ${result.message}")
                    }
                }
            } catch (e: Exception) {
                console.error("⚠️ Web ViewModel: Force-fetch exception: ${e.message}")
            }

            try {
                repository.observeActiveGoals(userId).collect { goalsList ->
                    console.log("📊 Web ViewModel: Flow emitted ${goalsList.size} goals")
                    goalsList.forEachIndexed { index, goal ->
                        console.log("   [$index] ${goal.name} (${goal.id})")
                    }
                    goals = goalsList
                    isLoading = false
                    errorMessage = null
                    notifyStateChanged()
                }
            } catch (e: Exception) {
                console.error("❌ Web ViewModel: Flow error: ${e.message}")
                errorMessage = e.message
                isLoading = false
                notifyStateChanged()
            }
        }
    }

    fun createGoal(
        name: String,
        category: GoalCategory,
        targetAmount: Double,
        deadline: JsDate,
        iconEmoji: String,
        priority: GoalPriority = GoalPriority.NICE_TO_HAVE,
        accountType: GoalAccountType? = null,
        onCreated: ((SavingsGoal) -> Unit)? = null,
    ) {
        scope.launch {
            val sanitizedAccountType = accountType?.takeIf { it in GoalAccountType.optionsFor(category) }
            val goal = createSavingsGoalFromJs(
                id = "goal-${Random.nextInt(100000, 999999)}",
                name = name,
                targetAmount = targetAmount,
                currentAmount = 0.0,
                category = category,
                priority = priority,
                deadline = deadline,
                iconEmoji = iconEmoji
            ).copy(accountType = sanitizedAccountType)

            console.log("💾 Web ViewModel: Creating goal '${name}' (${goal.id})")
            when (val result = repository.createGoal(goal)) {
                is DatabaseResult.Success -> {
                    console.log("✅ Web ViewModel: Goal created - Flow will auto-update")
                    onCreated?.invoke(goal)
                    // No need to call loadGoals() - reactive Flow will auto-emit
                }
                is DatabaseResult.Error -> {
                    console.error("❌ Web ViewModel: Failed to create goal: ${result.message}")
                    errorMessage = result.message
                    notifyStateChanged()
                }
            }
        }
    }

    fun deleteGoal(goalId: String) {
        scope.launch {
            val userId = userIdManager.getUserId()
            console.log("🗑️ Web ViewModel: Deleting goal $goalId")
            when (val result = repository.deleteGoal(goalId, userId)) {
                is DatabaseResult.Success -> {
                    console.log("✅ Web ViewModel: Goal deleted - Flow will auto-update")
                    // No need to call loadGoals() - reactive Flow will auto-emit
                }
                is DatabaseResult.Error -> {
                    console.error("❌ Web ViewModel: Failed to delete goal: ${result.message}")
                    errorMessage = result.message
                    notifyStateChanged()
                }
            }
        }
    }

    fun updateGoal(
        goal: SavingsGoal,
        name: String,
        targetAmount: Double,
        deadline: JsDate,
        // Wiz 3R AI fields — null preserves existing values? No: the form is
        // the source of truth on save, so passing null clears the field. The
        // edit modal always pre-populates from the goal, so omission means
        // user intentionally cleared it.
        category: GoalCategory = goal.category,
        priority: GoalPriority = goal.priority,
        iconEmoji: String = goal.iconEmoji ?: "",
        accountType: GoalAccountType? = goal.accountType,
        debtInterestRate: Double? = goal.debtInterestRate,
        debtRemainingBalance: Double? = goal.debtRemainingBalance,
        debtRemainingMonths: Int? = goal.debtRemainingMonths,
        debtPaymentFrequency: PaymentFrequency? = goal.debtPaymentFrequency,
        debtMonthlyEquivalentPayment: Double? = goal.debtMonthlyEquivalentPayment,
    ) {
        scope.launch {
            // Create updated goal with new values, preserving other fields.
            // If the category just changed, drop any account_type that no longer
            // makes sense for the new category — saves the user from a stale
            // value (e.g. they had FHSA under HOME_PURCHASE, now switched to
            // DEBT_PAYOFF which has no account options).
            val sanitizedAccountType = accountType?.takeIf { it in GoalAccountType.optionsFor(category) }

            val updatedGoal = createSavingsGoalFromJs(
                id = goal.id,
                name = name,
                targetAmount = targetAmount,
                currentAmount = goal.currentAmount,
                category = category,
                priority = priority,
                deadline = deadline,
                iconEmoji = iconEmoji
            ).copy(
                accountType = sanitizedAccountType,
                debtInterestRate = debtInterestRate,
                debtRemainingBalance = debtRemainingBalance,
                debtRemainingMonths = debtRemainingMonths,
                debtPaymentFrequency = debtPaymentFrequency,
                debtMonthlyEquivalentPayment = debtMonthlyEquivalentPayment,
            )

            console.log("💾 Web ViewModel: Updating goal '${name}' (${goal.id}) — cat=${category.name} account=${sanitizedAccountType?.name ?: "null"}")
            when (val result = repository.updateGoal(updatedGoal)) {
                is DatabaseResult.Success -> {
                    console.log("✅ Web ViewModel: Goal updated - Flow will auto-update")
                    // No need to call loadGoals() - reactive Flow will auto-emit
                }
                is DatabaseResult.Error -> {
                    console.error("❌ Web ViewModel: Failed to update goal: ${result.message}")
                    errorMessage = result.message
                    notifyStateChanged()
                }
            }
        }
    }

    fun clearError() {
        errorMessage = null
        notifyStateChanged()
    }

    private fun notifyStateChanged() {
        onStateChanged?.invoke()
    }
}

// Extension functions for goal display
fun SavingsGoal.progressPercentage(): Double {
    return analytics.goalProgressBarPercent(this)
}

fun SavingsGoal.categoryDisplayName(): String {
    return when (category) {
        GoalCategory.EMERGENCY_FUND -> i18n.StringKey.GOAL_DISPLAY_EMERGENCY_FUND.tr()
        GoalCategory.VACATION -> i18n.StringKey.GOAL_DISPLAY_VACATION.tr()
        GoalCategory.HOME_PURCHASE -> i18n.StringKey.GOAL_DISPLAY_HOME_PURCHASE.tr()
        GoalCategory.RETIREMENT -> i18n.StringKey.GOAL_DISPLAY_RETIREMENT.tr()
        GoalCategory.EDUCATION -> i18n.StringKey.GOAL_DISPLAY_EDUCATION.tr()
        GoalCategory.MAJOR_PURCHASE -> i18n.StringKey.GOAL_DISPLAY_MAJOR_PURCHASE.tr()
        GoalCategory.INVESTMENT -> i18n.StringKey.GOAL_DISPLAY_INVESTMENT.tr()
        GoalCategory.DEBT_PAYOFF -> i18n.StringKey.GOAL_DISPLAY_DEBT_PAYOFF.tr()
        GoalCategory.OTHER -> i18n.StringKey.GOAL_DISPLAY_OTHER.tr()
    }
}

fun SavingsGoal.daysRemainingText(): String {
    val days = this.getDaysRemaining()
    return "$days days left"
}
